import React, { useEffect, useState, useRef } from 'react';
import {
  Box,
  Typography,
  LinearProgress,
  Alert,
  Chip
} from '@mui/material';
import { LeadImportReportDTO } from '../../api/generated/requests/types.gen';
import { AuthService } from '../../services/authService';

interface ImportProgressTrackerProps {
  importId: string;
  onComplete: (report: LeadImportReportDTO) => void;
  onError: (error: string) => void;
}

const ImportProgressTracker: React.FC<ImportProgressTrackerProps> = ({
  importId,
  onComplete,
  onError
}) => {
  const [progress, setProgress] = useState(0);
  const [status, setStatus] = useState<'connecting' | 'importing' | 'completed' | 'error'>('connecting');
  const [totalRecords, setTotalRecords] = useState<number>(0);
  const [processedRecords, setProcessedRecords] = useState<number>(0);
  const [successfullyImported, setSuccessfullyImported] = useState<number>(0);
  const [duplicatesRejected, setDuplicatesRejected] = useState<number>(0);
  const [errorsCount, setErrorsCount] = useState<number>(0);
  const [currentOperation, setCurrentOperation] = useState<string>('');
  const eventSourceRef = useRef<EventSource | null>(null);
  const importCompletedRef = useRef(false);

  useEffect(() => {
    if (!importId) return;

    // Reset completion flag
    importCompletedRef.current = false;
    setStatus('connecting');

    // Create SSE connection with Authorization header
    const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080';
    const token = AuthService.getToken();
    const sseUrl = `${API_URL}/api/lead-contacts/import-progress/${importId}`;
    
    // Custom SSE implementation with Authorization header support
    const createCustomEventSource = async () => {
      try {
        const response = await fetch(sseUrl, {
          method: 'GET',
          headers: {
            'Accept': 'text/event-stream',
            'Cache-Control': 'no-cache',
            'Authorization': `Bearer ${token}`
          }
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const reader = response.body?.getReader();
        if (!reader) {
          throw new Error('No response body reader available');
        }

        // Create a custom event source object
        const customEventSource = {
          close: () => {
            reader.cancel();
          },
          readyState: 1 // OPEN
        };

        eventSourceRef.current = customEventSource as any;

        // Trigger onopen event
        setStatus('importing');

        // Read the stream
        const decoder = new TextDecoder();
        let buffer = '';

        const processStream = async () => {
          try {
            while (true) {
              const { done, value } = await reader.read();
              
              if (done) break;
              
              buffer += decoder.decode(value, { stream: true });
              const lines = buffer.split('\n');
              buffer = lines.pop() || ''; // Keep incomplete line in buffer

              for (const line of lines) {
                if (line.trim() === '') continue;
                
                if (line.startsWith('data: ')) {
                  const eventData = line.substring(6);
                  handleSSEData(eventData, 'Custom SSE message');
                } else if (line.startsWith('event: ')) {
                  // Handle different event types if needed
                }
              }
            }
          } catch (error) {
            if (!importCompletedRef.current) {
              setStatus('error');
              const errorMsg = 'Błąd połączenia z serwerem';
              onError(errorMsg);
            }
          }
        };

        processStream();
        
      } catch (error) {
        if (!importCompletedRef.current) {
          setStatus('error');
          const errorMsg = 'Błąd połączenia z serwerem';
          onError(errorMsg);
        }
      }
    };

    createCustomEventSource();

    // Handle import completion - exactly like HTML version
    const handleImportCompletion = (data: any) => {
      // WAŻNE: Ustaw flagę zakończenia PRZED zamknięciem połączenia
      importCompletedRef.current = true;

      if (eventSourceRef.current) {
        eventSourceRef.current.close();
      }

      if (data.status === 'COMPLETED') {
        setStatus('completed');
        if (data.finalReport) {
          onComplete(data.finalReport);
        }
      } else {
        setStatus('error');
        const errorMsg = data.currentOperation || 'Nieznany błąd podczas importu';
        onError(errorMsg);
      }
    };

    // Funkcja do obsługi danych z SSE (używana w obu handlerach)
    const handleSSEData = (eventData: string, source: string) => {
      if (importCompletedRef.current) return;

      try {
        const data = JSON.parse(eventData);
        updateProgress(data);

        // Handle completion - SPRAWDŹ I USTAW FLAGĘ TUTAJ
        if (data.status === 'COMPLETED' || data.status === 'ERROR') {
          handleImportCompletion(data);
        }
      } catch (e) {
        // Ignore parsing errors during completion
        if (!importCompletedRef.current) {
          setStatus('error');
          const errorMsg = 'Błąd parsowania danych z serwera';
          onError(errorMsg);
        }
      }
    };


    // Cleanup on unmount
    return () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
      }
    };
  }, [importId, onComplete, onError]);

  const updateProgress = (data: any) => {
    // Update progress bar
    const percentage = Math.round(data.progressPercentage || 0);
    setProgress(percentage);

    // Update all statistics from SSE data with safe defaults
    setTotalRecords(data.totalRecords ?? 0);
    setProcessedRecords(data.processedRecords ?? 0);
    setSuccessfullyImported(data.successfullyImported ?? 0);
    setDuplicatesRejected(data.duplicatesRejected ?? 0);
    setErrorsCount(data.errorsCount ?? 0);
    setCurrentOperation(data.currentOperation || '');
  };

  const getStatusColor = () => {
    switch (status) {
      case 'connecting':
        return 'info';
      case 'importing':
        return 'primary';
      case 'completed':
        return 'success';
      case 'error':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusText = () => {
    switch (status) {
      case 'connecting':
        return 'Łączenie...';
      case 'importing':
        return 'Importowanie...';
      case 'completed':
        return 'Zakończono';
      case 'error':
        return 'Błąd';
      default:
        return 'Nieznany';
    }
  };

  return (
    <Box>
      <Box sx={{ mb: 2 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
          <Typography variant="h6">
            Postęp importu
          </Typography>
          <Chip 
            label={getStatusText()} 
            color={getStatusColor() as any}
            size="small"
          />
        </Box>
        
        <LinearProgress 
          variant="determinate" 
          value={progress} 
          sx={{ mb: 1 }}
        />
        
        <Box display="flex" justifyContent="space-between">
          <Typography variant="body2" color="text.secondary">
            {progress.toFixed(1)}%
          </Typography>
          {totalRecords > 0 && (
            <Typography variant="body2" color="text.secondary">
              {processedRecords} / {totalRecords} rekordów
            </Typography>
          )}
        </Box>

        {/* Current Operation Display */}
        {currentOperation && status === 'importing' && (
          <Box sx={{ mt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              {currentOperation}
            </Typography>
          </Box>
        )}


        {/* Comprehensive Statistics Display */}
        {totalRecords > 0 && (
          <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
            <Typography variant="subtitle2" gutterBottom>
              Statystyki importu
            </Typography>
            <Box sx={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', 
              gap: 2 
            }}>
              <Box sx={{ textAlign: 'center', p: 1, bgcolor: 'white', borderRadius: 1 }}>
                <Typography variant="h6" color="primary">
                  {totalRecords}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  ŁĄCZNIE
                </Typography>
              </Box>
              <Box sx={{ textAlign: 'center', p: 1, bgcolor: 'white', borderRadius: 1 }}>
                <Typography variant="h6" color="info.main">
                  {processedRecords}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  PRZETWORZONO
                </Typography>
              </Box>
              <Box sx={{ textAlign: 'center', p: 1, bgcolor: 'white', borderRadius: 1 }}>
                <Typography variant="h6" color="success.main">
                  {successfullyImported}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  ZAIMPORTOWANO
                </Typography>
              </Box>
              <Box sx={{ textAlign: 'center', p: 1, bgcolor: 'white', borderRadius: 1 }}>
                <Typography variant="h6" color="warning.main">
                  {duplicatesRejected}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  DUPLIKATY
                </Typography>
              </Box>
              <Box sx={{ textAlign: 'center', p: 1, bgcolor: 'white', borderRadius: 1 }}>
                <Typography variant="h6" color="error.main">
                  {errorsCount}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  BŁĘDY
                </Typography>
              </Box>
            </Box>
          </Box>
        )}
      </Box>

      {/* Success Alert for Completed Import */}
      {status === 'completed' && (
        <Alert severity="success" sx={{ mt: 2 }}>
          <Typography variant="body2">
            <strong>Import zakończony pomyślnie!</strong>
          </Typography>
          {totalRecords > 0 && (
            <Typography variant="body2" sx={{ mt: 1 }}>
              Przetworzono {totalRecords} rekordów • 
              Zaimportowano {successfullyImported} • 
              Duplikaty {duplicatesRejected} • 
              Błędy {errorsCount}
            </Typography>
          )}
        </Alert>
      )}

      {/* Error Alert */}
      {status === 'error' && (
        <Alert severity="error" sx={{ mt: 2 }}>
          <Typography variant="body2">
            Import został przerwany z powodu błędu. Spróbuj ponownie.
          </Typography>
        </Alert>
      )}
    </Box>
  );
};

export default ImportProgressTracker;